import { useState } from "react";
import { toast } from "sonner";
import { useDolfinClient } from "@/components/DolfinProvider";
import { apiErrorMessage } from "@/lib/api-error";
import { SearchCombobox } from "@/components/SearchCombobox";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Spinner } from "@/components/ui/spinner";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";

export interface NewSupplierCreditNoteDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onCreated: () => void;
  /** Pre-selects and locks the supplier. */
  fixedSupplierId?: string;
}

const CURRENCIES = ["GBP", "USD", "EUR"];

export function NewSupplierCreditNoteDialog({
  open,
  onOpenChange,
  onCreated,
  fixedSupplierId,
}: NewSupplierCreditNoteDialogProps) {
  const api = useDolfinClient();
  const [supplierId, setSupplierId] = useState(fixedSupplierId ?? "");
  const [amount, setAmount] = useState("");
  const [currency, setCurrency] = useState("GBP");
  const [reason, setReason] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  function resetForm() {
    setSupplierId(fixedSupplierId ?? "");
    setAmount("");
    setCurrency("GBP");
    setReason("");
    setSubmitting(false);
    setError(null);
  }

  function handleOpenChange(next: boolean) {
    if (!next) resetForm();
    onOpenChange(next);
  }

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    setError(null);

    if (!supplierId) {
      setError("Select a supplier.");
      return;
    }
    const amt = parseFloat(amount);
    if (!Number.isFinite(amt) || amt <= 0) {
      setError("Enter an amount greater than zero.");
      return;
    }

    setSubmitting(true);
    const { data, error: apiError } = await api.POST("/v1/suppliers/{supplierId}/credit-notes", {
      params: { path: { supplierId } },
      body: { amount: amt, currency, reason: reason.trim() || null },
    });

    if (apiError || !data) {
      const message = apiErrorMessage(apiError, "Failed to create supplier credit note.");
      setError(message);
      toast.error(message);
      setSubmitting(false);
      return;
    }

    setSubmitting(false);
    handleOpenChange(false);
    toast.success("Supplier credit note created (Draft)");
    onCreated();
  }

  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      <DialogContent className="max-w-md">
        <form onSubmit={handleSubmit}>
          <DialogHeader>
            <DialogTitle>New supplier credit note</DialogTitle>
            <DialogDescription>
              Record credit a supplier owes you. It's created as a Draft - issue it to make the credit
              available, then apply it to a bill.
            </DialogDescription>
          </DialogHeader>

          <div className="mt-4 space-y-4">
            {!fixedSupplierId && (
              <div className="space-y-2">
                <Label htmlFor="scn-supplier">Supplier</Label>
                <SearchCombobox
                  value={supplierId}
                  onSelect={(id) => setSupplierId(id)}
                  placeholder="Select a supplier"
                  searchPlaceholder="Search suppliers…"
                  emptyText="No suppliers found"
                  search={async (q) => {
                    const { data } = await api.GET("/v1/suppliers", {
                      params: { query: { Name: q, Limit: 20 } },
                    });
                    return (data?.data ?? []).map((s) => ({
                      id: s.id!,
                      name: s.name,
                      sublabel: s.email,
                    }));
                  }}
                />
              </div>
            )}

            <div className="grid grid-cols-3 gap-3">
              <div className="col-span-2 space-y-2">
                <Label htmlFor="scn-amount">Amount (gross)</Label>
                <Input
                  id="scn-amount"
                  type="number"
                  step="0.01"
                  min="0"
                  placeholder="0.00"
                  value={amount}
                  onChange={(e) => setAmount(e.target.value)}
                  autoFocus
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="scn-currency">Currency</Label>
                <Select value={currency} onValueChange={setCurrency}>
                  <SelectTrigger id="scn-currency">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {CURRENCIES.map((c) => (
                      <SelectItem key={c} value={c}>
                        {c}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>

            <div className="space-y-2">
              <Label htmlFor="scn-reason">Reason (optional)</Label>
              <Input
                id="scn-reason"
                placeholder="e.g. faulty goods returned"
                value={reason}
                onChange={(e) => setReason(e.target.value)}
              />
            </div>

            {error && <p className="text-sm text-destructive">{error}</p>}
          </div>

          <DialogFooter className="mt-6">
            <Button type="submit" disabled={submitting}>
              {submitting && <Spinner className="size-4" />}
              Create draft
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
